import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Button, Table, Alert, Badge, Spinner } from 'react-bootstrap';
import { API_BASE } from '../lib/apiBase';

const AdminApprovals = () => {
  const [events, setEvents] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [eventRes, jobRes] = await Promise.all([
        axios.get(`${API_BASE}/events/pending`, { headers: { 'x-user-role': 'admin' } }),
        axios.get(`${API_BASE}/jobs/pending`, { headers: { 'x-user-role': 'admin' } }),
      ]);
      setEvents(Array.isArray(eventRes.data) ? eventRes.data : []);
      setJobs(Array.isArray(jobRes.data) ? jobRes.data : []);
    } catch (err) {
      console.error('Failed to load approvals:', err);
      setError('Failed to load pending approvals. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('userRole') === 'admin') {
      loadData();
    }
  }, [loadData]);

  const handleEventAction = async (id, action) => {
    try {
      await axios.post(`${API_BASE}/events/${id}/${action}`, {}, { headers: { 'x-user-role': 'admin' } });
      await loadData();
    } catch (err) {
      console.error(`Failed to ${action} event:`, err);
      setError(`Failed to ${action} event.`);
    }
  };

  const handleJobAction = async (id, action) => {
    try {
      await axios.post(`${API_BASE}/jobs/${id}/${action}`, {}, { headers: { 'x-user-role': 'admin' } });
      await loadData();
    } catch (err) {
      console.error(`Failed to ${action} job:`, err);
      setError(`Failed to ${action} job.`);
    }
  };

  const PendingSection = ({ title, items, columns, renderRow, emptyLabel }) => (
    <Card className="shadow-sm border-0 h-100">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-0">{title}</h5>
            <small className="text-muted">{items.length} awaiting review</small>
          </div>
        </div>
        {items.length === 0 ? (
          <div className="text-center py-4 text-muted">{emptyLabel}</div>
        ) : (
          <Table responsive bordered hover size="sm">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(renderRow)}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );

  if (sessionStorage.getItem('userRole') !== 'admin') {
    return (
      <Container className="py-5">
        <Alert variant="warning">Only admin users can access the approval queue.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h2 className="mb-4">Awaiting Approval</h2>
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      <Row className="g-4">
        <Col md={12} lg={6}>
          <PendingSection
            title="Event Changes"
            items={events}
            columns={['Title', 'Type', 'Submitted By', 'Actions']}
            emptyLabel="No events waiting for approval."
            renderRow={(event) => {
              const isNew = event.status === 'pending';
              const isUpdate = Boolean(event.pendingChange?.changeType);
              const proposed = event.pendingChange?.data || {};
              const proposedDate = proposed.date ? new Date(proposed.date) : null;
              const dateChanged = proposedDate && new Date(event.date).toDateString() !== proposedDate.toDateString();
              const titleChanged = proposed.title && proposed.title !== event.title;
              const descriptionChanged = proposed.description && proposed.description !== event.description;
              return (
                <tr key={event._id || event.id}>
                  <td>
                    <div className="fw-semibold">{event.title}</div>
                    <div className="small text-muted">{new Date(event.date).toLocaleDateString()}</div>
                    {titleChanged && (
                      <div className="small text-muted">Proposed title: {proposed.title}</div>
                    )}
                    {dateChanged && (
                      <div className="small text-muted">Proposed date: {proposedDate.toLocaleDateString()}</div>
                    )}
                    {descriptionChanged && (
                      <div className="small text-muted">Description preview: {proposed.description.slice(0, 80)}{proposed.description.length > 80 ? '…' : ''}</div>
                    )}
                  </td>
                  <td>
                    {isNew && <Badge bg="warning">New</Badge>}
                    {isUpdate && <Badge bg="info">Update</Badge>}
                  </td>
                  <td>{event.submittedBy}</td>
                  <td className="d-flex gap-2">
                    <Button size="sm" variant="success" onClick={() => handleEventAction(event._id || event.id, 'approve')}>
                      Approve
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleEventAction(event._id || event.id, 'reject')}>
                      Reject
                    </Button>
                  </td>
                </tr>
              );
            }}
          />
        </Col>
        <Col md={12} lg={6}>
          <PendingSection
            title="Job Changes"
            items={jobs}
            columns={['Title', 'Type', 'Submitted By', 'Actions']}
            emptyLabel="No jobs waiting for approval."
            renderRow={(job) => {
              const isNew = job.status === 'pending';
              const changeType = job.pendingChange?.changeType;
              const isUpdate = changeType === 'update';
              const isDelete = changeType === 'delete';
              const proposed = isUpdate ? (job.pendingChange?.data || {}) : {};
              const titleChanged = proposed.title && proposed.title !== job.title;
              return (
                <tr key={job._id || job.id}>
                  <td>
                    <div className="fw-semibold">{job.title}</div>
                    {titleChanged && (
                      <div className="small text-muted">Proposed title: {proposed.title}</div>
                    )}
                    {isDelete && (
                      <div className="small text-danger">Pending deletion approval</div>
                    )}
                  </td>
                  <td>
                    {isNew && <Badge bg="warning">New</Badge>}
                    {isUpdate && <Badge bg="info">Update</Badge>}
                    {isDelete && <Badge bg="danger">Delete</Badge>}
                  </td>
                  <td>{job.submittedBy}</td>
                  <td className="d-flex gap-2">
                    <Button size="sm" variant="success" onClick={() => handleJobAction(job._id || job.id, 'approve')}>
                      Approve
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleJobAction(job._id || job.id, 'reject')}>
                      Reject
                    </Button>
                  </td>
                </tr>
              );
            }}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default AdminApprovals;
